import type { OrganizationMetadataEntry } from '@/utils/organizationMetadata'
import { isRecord } from '@/utils/typeGuards'

export const TEAM_REVIEW_WORKFLOW_POLICY_V1_KEY =
  'TEAM_REVIEW_WORKFLOW_POLICY_V1'

type TeamReviewWorkflowRule = {
  manualCreation?: {
    requiresReview?: boolean
  }
}

export type TeamReviewWorkflowPolicyV1 = {
  version: 1
  options?: TeamReviewWorkflowRule
  references?: TeamReviewWorkflowRule
}

export type ParsedTeamReviewWorkflowPolicy = {
  policy: TeamReviewWorkflowPolicyV1 | null
  source: 'none' | 'policy'
  error: string | null
}

const DEFAULT_TEAM_REVIEW_WORKFLOW_POLICY: TeamReviewWorkflowPolicyV1 = {
  version: 1,
  options: {
    manualCreation: {
      requiresReview: true,
    },
  },
  references: {
    manualCreation: {
      requiresReview: true,
    },
  },
}

const hasOwnKeys = (value: Record<string, unknown>) =>
  Object.keys(value).length > 0

const getRuleOverride = (
  rule: TeamReviewWorkflowRule | undefined,
  defaultRule: TeamReviewWorkflowRule,
): TeamReviewWorkflowRule | undefined => {
  const manualCreation: NonNullable<TeamReviewWorkflowRule['manualCreation']> =
    {}

  if (
    rule?.manualCreation?.requiresReview !== undefined &&
    rule.manualCreation.requiresReview !==
      defaultRule.manualCreation?.requiresReview
  ) {
    manualCreation.requiresReview = rule.manualCreation.requiresReview
  }

  return hasOwnKeys(manualCreation) ? { manualCreation } : undefined
}

const parseWorkflowRule = (
  value: unknown,
  label: 'options' | 'references',
): { rule?: TeamReviewWorkflowRule; error?: string } => {
  if (value === undefined) {
    return {}
  }

  if (!isRecord(value)) {
    return {
      error: `${label} policy must be an object.`,
    }
  }

  const manualCreation = value.manualCreation
  if (manualCreation === undefined) {
    return {}
  }

  if (!isRecord(manualCreation)) {
    return {
      error: `${label}.manualCreation must be an object.`,
    }
  }

  const requiresReview = manualCreation.requiresReview
  if (requiresReview !== undefined && typeof requiresReview !== 'boolean') {
    return {
      error: `${label}.manualCreation.requiresReview must be a boolean.`,
    }
  }

  return {
    rule: {
      ...(requiresReview !== undefined
        ? {
            manualCreation: {
              requiresReview,
            },
          }
        : {}),
    },
  }
}

export const getDefaultTeamReviewWorkflowPolicy =
  (): TeamReviewWorkflowPolicyV1 => ({
    version: 1,
    options: {
      manualCreation: {
        requiresReview: true,
      },
    },
    references: {
      manualCreation: {
        requiresReview: true,
      },
    },
  })

export const resolveTeamReviewWorkflowPolicy = (
  policy: TeamReviewWorkflowPolicyV1 | null | undefined,
): TeamReviewWorkflowPolicyV1 => ({
  version: 1,
  options: {
    manualCreation: {
      requiresReview:
        policy?.options?.manualCreation?.requiresReview ??
        DEFAULT_TEAM_REVIEW_WORKFLOW_POLICY.options?.manualCreation
          ?.requiresReview ??
        true,
    },
  },
  references: {
    manualCreation: {
      requiresReview:
        policy?.references?.manualCreation?.requiresReview ??
        DEFAULT_TEAM_REVIEW_WORKFLOW_POLICY.references?.manualCreation
          ?.requiresReview ??
        true,
    },
  },
})

export const parseTeamReviewWorkflowPolicy = (
  value: unknown,
): ParsedTeamReviewWorkflowPolicy => {
  if (value == null) {
    return {
      policy: null,
      source: 'none',
      error: null,
    }
  }

  if (!isRecord(value)) {
    return {
      policy: null,
      source: 'policy',
      error: 'Review workflow policy must be an object.',
    }
  }

  if (value.version !== 1) {
    return {
      policy: null,
      source: 'policy',
      error: 'Review workflow policy version must be 1.',
    }
  }

  const optionsResult = parseWorkflowRule(value.options, 'options')
  if (optionsResult.error) {
    return {
      policy: null,
      source: 'policy',
      error: optionsResult.error,
    }
  }

  const referencesResult = parseWorkflowRule(value.references, 'references')
  if (referencesResult.error) {
    return {
      policy: null,
      source: 'policy',
      error: referencesResult.error,
    }
  }

  return {
    policy: {
      version: 1,
      ...(optionsResult.rule ? { options: optionsResult.rule } : {}),
      ...(referencesResult.rule ? { references: referencesResult.rule } : {}),
    },
    source: 'policy',
    error: null,
  }
}

export const serializeTeamReviewWorkflowPolicy = (
  policy: TeamReviewWorkflowPolicyV1 | null | undefined,
): TeamReviewWorkflowPolicyV1 | null => {
  if (!policy) return null

  const resolved = resolveTeamReviewWorkflowPolicy(policy)
  const nextPolicy: TeamReviewWorkflowPolicyV1 = {
    version: 1,
  }

  const options = getRuleOverride(
    resolved.options,
    DEFAULT_TEAM_REVIEW_WORKFLOW_POLICY.options ?? {},
  )
  if (options) {
    nextPolicy.options = options
  }

  const references = getRuleOverride(
    resolved.references,
    DEFAULT_TEAM_REVIEW_WORKFLOW_POLICY.references ?? {},
  )
  if (references) {
    nextPolicy.references = references
  }

  return hasOwnKeys(nextPolicy as Record<string, unknown>) &&
    (nextPolicy.options || nextPolicy.references)
    ? nextPolicy
    : null
}

export const hasTeamReviewWorkflowPolicyOverrides = (
  policy: TeamReviewWorkflowPolicyV1 | null | undefined,
) => Boolean(serializeTeamReviewWorkflowPolicy(policy))

export const readTeamReviewWorkflowPolicyFromMetadata = (
  metadata: OrganizationMetadataEntry[],
): ParsedTeamReviewWorkflowPolicy => {
  const entry = metadata.find(
    (item) => item.key === TEAM_REVIEW_WORKFLOW_POLICY_V1_KEY,
  )

  if (!entry) {
    return {
      policy: null,
      source: 'none',
      error: null,
    }
  }

  return parseTeamReviewWorkflowPolicy(entry.value)
}
